import React, { useEffect, useState } from "react";
import { DatePickerInput, Label, Modal } from "../../../components/ui";
import {
  GetEventListService,
  CreateEventService,
  checkHashTagService,
} from "../../../apis/events";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Button, Form, Input, Pagination, Select, Switch, Tooltip } from "antd";
import dayjs from "dayjs";
import {
  FIRST_MESSAGE,
  HASTAG_REGEX,
  StatusFilterOptions,
} from "../../../config/constant";
import { useNavigate, useSearchParams } from "react-router-dom";
import EventListSkeleton from "../../../components/common/EventListSkeleton";
import { EventCard, NoEventComponent } from "../../../components/event";
import { tostE, tostS } from "../../../config/toastConfig";
import { useSelector } from "react-redux";
import { scrollToTop } from "../../../utils/commonFunctions";

export default function EventListing() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [hashAvailable, setHashAvailable] = useState(null);
  const [hashTerm, setHashTerm] = useState("");
  const [checking, setChecking] = useState(false);
  const { user } = useSelector((state) => state.auth);

  const getEvents = async (payload = {}) => {
    setLoading(true);
    try {
      const body = { page, ...payload };
      if (statusFilter && statusFilter !== "") {
        body.status = statusFilter;
      }
      const res = await GetEventListService(body);
      if (res.status === 200 && res.data.success) {
        setData(res.data.event_list.data);
        setTotal(res.data.event_list.total);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlPage = Number(searchParams.get("page")) || 1;
    if (urlPage !== page) {
      setPage(urlPage);
      return;
    }
    const q = (appliedSearch || "").trim();
    if (q.length > 0) {
      getEvents({ search: q });
    } else {
      getEvents();
    }
  }, [page, statusFilter, searchParams, appliedSearch]); // eslint-disable-line

  const handleApplyFilter = () => {
    const q = (searchTerm || "").trim();
    setAppliedSearch(q);
    if (page !== 1) {
      setPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", "1");
        return next;
      });
    }
  };

  const CreateEventSchema = Yup.object().shape({
    event_name: Yup.string().required("Event name is required"),
    hastag: Yup.string().required("Hashtag is required"),
    // .matches(HASTAG_REGEX, "Hashtag is invalid"),
    start_date: Yup.date().required("Start date is required"),
    end_date: Yup.date().required("End date is required"),
    first_message: Yup.string()
      .required("First message is required")
      .test(
        "single-hashtag",
        "The message must contain exactly one hashtag.",
        (value) => {
          const hashtagCount = (value.match(/#/g) || []).length;
          return hashtagCount === 1;
        },
      ),
  });

  const formik = useFormik({
    initialValues: {
      event_name: "",
      hastag: "",
      start_date: "",
      end_date: "",
      first_message: FIRST_MESSAGE,
      show_event_name: false,
    },
    validationSchema: CreateEventSchema,
    onSubmit: async (values, { resetForm, setSubmitting, setErrors }) => {
      if (!hashAvailable) {
        return;
      }

      let body = {
        event_title: values.event_name,
        hastag: values.hastag,
        start_date: values.start_date.format("YYYY-MM-DD"),
        end_date: values.end_date.format("YYYY-MM-DD"),
        first_message: values.first_message || FIRST_MESSAGE,
        source: "manual",
        show_event_name: values.show_event_name ? 1 : 0,
      };
      try {
        let res = await CreateEventService(body);
        if (res.status === 200 && res.data.success) {
          tostS(res.data.message);
          setShowModal(false);
          getEvents();
          resetForm();
        } else {
          // Map API errors to Formik fields
          if (res.data.message) {
            const fieldErrors = {};
            Object.keys(res.data.message).forEach((key) => {
              fieldErrors[key] = res.data.message[key][0]; // Assuming the error is in an array
            });
            setErrors(fieldErrors); // Set errors in Formik
          }
          tostE("Please fix the errors in the form.");
        }
      } catch (error) {
        tostE(error.response?.data?.message || "An error occurred.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    setValues,
    errors,
    touched,
    getFieldProps,
    setFieldValue,
    handleSubmit,
    resetForm,
    isSubmitting,
  } = formik;

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (hashTerm === "") return;
      setChecking(true);
      try {
        const checkHash = await checkHashTagService({ hastag: hashTerm });
        if (checkHash.status === 200 && !checkHash.data.success) {
          setHashAvailable(false);
          return;
        } else {
          setHashAvailable(true);
          setFieldValue("hastag", hashTerm);
          return;
        }
      } catch (error) {
        console.log(error);
      } finally {
        setChecking(false);
      }
    }, 1000);
    return () => {
      clearTimeout(timer);
    };
  }, [hashTerm]); // eslint-disable-line

  // Handle paste into hashtag input: inserts pasted text at cursor and appends a space
  const handleHashtagPaste = (e) => {
    e.preventDefault();
    const pasted =
      (e.clipboardData || window.clipboardData)?.getData("text") || "";
    const target = e.target;
    const start = target.selectionStart ?? hashTerm.length;
    const end = target.selectionEnd ?? start;
    const insertText = `${pasted.trim()} `; // add space after paste
    const newHash = `${hashTerm.slice(0, start)}${insertText}${hashTerm.slice(end)}`;
    setHashTerm(newHash);
    setFieldValue("hastag", newHash);

    if (!values.first_message) return;
    const regex = /(from\s+)(#\S+)?/i;
    if (regex.test(values.first_message)) {
      const updatedMessage = values.first_message.replace(
        regex,
        (_, prefix) => `${prefix}#${newHash}`,
      );
      setFieldValue("first_message", updatedMessage);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center">
        <h3 className="page-title">Event Listing</h3>
        {user?.access_level === 1 && (
          <Button
            type="primary"
            onClick={() => setShowModal(true)}
            title="Create Event"
          >
            <i className="fa-solid fa-plus"></i>
            Create Event
          </Button>
        )}
      </div>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <p className="page-subtitle">
          {statusFilter ? statusFilter : "All"} Events
        </p>
        <div
          className="d-flex align-items-center gap-2"
          style={{ minWidth: 360 }}
        >
          <Select
            title="Status Filter"
            options={StatusFilterOptions}
            value={statusFilter}
            style={{ width: 120 }}
            onChange={(value) => {
              setStatusFilter(value);
              if (page !== 1) {
                setPage(1);
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set("page", "1");
                  return next;
                });
              }
            }}
            placeholder="Status"
          />
          <Input
            allowClear
            placeholder="Search events"
            value={searchTerm}
            onChange={(e) => {
              const val = e.target.value;
              setSearchTerm(val);
              if (val.trim().length === 0) {
                setAppliedSearch("");
                if (page !== 1) {
                  setPage(1);
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("page", "1");
                    return next;
                  });
                }
                // If search cleared, fetch full list
                getEvents();
              }
            }}
            onPressEnter={handleApplyFilter}
            style={{ width: "260px" }}
          />
          <Button
            type="default"
            loading={loading}
            onClick={handleApplyFilter}
            icon={<i className="fa-solid fa-filter"></i>}
          />
        </div>
      </div>
      <div className="main-content">
        {!loading ? (
          data.length > 0 ? (
            <>
              {data.map((item, index) => (
                <EventCard
                  key={index}
                  data={item}
                  manageEvent={(data) => {
                    navigate(`/app/events/manage/${data.id}/overview`, {
                      state: { from: `/app/events${page > 1 ? `?page=${page}` : ""}` },
                    });
                  }}
                />
              ))}
              <Pagination
                current={page}
                total={total}
                align="end"
                hideOnSinglePage
                responsive
                onChange={(page) => {
                  setPage(page);
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set("page", String(page));
                    return next;
                  });
                  scrollToTop();
                }}
              />
            </>
          ) : (
            <>
              <NoEventComponent accessLevel={user?.access_level} />
            </>
          )
        ) : (
          <EventListSkeleton />
        )}
      </div>
      <Modal
        modalTitle={"Create Event"}
        open={showModal}
        confirmLoading={isSubmitting}
        handleConfirm={() => {
          handleSubmit();
        }}
        handleCancel={() => {
          resetForm();
          setShowModal(false);
          setHashTerm("");
        }}
        okText={"Create Event"}
      >
        <FormikProvider value={formik}>
          <Form layout="vertical" onFinish={handleSubmit}>
            <Label
              label="Event Name"
              error={touched.event_name && errors.event_name}
              helperText={
                touched.event_name && errors.event_name ? errors.event_name : ""
              }
              required={true}
            >
              <Input
                placeholder="Enter event name"
                {...getFieldProps("event_name")}
              />
            </Label>
            <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "-8px 0 16px" }}>
              <Switch
                size="small"
                checked={values.show_event_name}
                onChange={(checked) => setFieldValue("show_event_name", checked)}
              />
              <span style={{ fontSize: 13 }}>Show event name on web page?</span>
            </div>
            <Label
              label="Hashtag"
              error={touched.hastag && errors.hastag}
              helperText={touched.hastag && errors.hastag ? errors.hastag : ""}
              required={true}
            >
              <Input
                autoComplete="off"
                addonBefore={"#"}
                placeholder="Enter hashtag"
                name="hashtag"
                value={hashTerm}
                onChange={(e) => {
                  const newHash = e.target.value;
                  setHashTerm(newHash);
                  setFieldValue("hastag", newHash);

                  if (!values.first_message) return;

                  // Match: "from" + space + (optional hashtag or word after it)
                  const regex = /(from\s+)(#\S+)?/i;

                  if (regex.test(values.first_message)) {
                    const updatedMessage = values.first_message.replace(
                      regex,
                      (_, prefix) => `${prefix}#${newHash}`,
                    );
                    setFieldValue("first_message", updatedMessage);
                  }
                  // If "from" not found, do nothing
                }}
                onPaste={handleHashtagPaste}
              />

              {hashTerm !== "" && !checking && hashAvailable !== null && (
                <>
                  {hashAvailable ? (
                    <p className="text-success">You made a great choice</p>
                  ) : (
                    <p className="text-danger">
                      Sorry! This hashtag is already taken
                    </p>
                  )}
                </>
              )}
            </Label>
            <Label
              label="Start Date"
              error={touched.start_date && errors.start_date}
              helperText={
                touched.start_date && errors.start_date ? errors.start_date : ""
              }
              required={true}
            >
              <DatePickerInput
                placeholder="Select Start Date"
                showNow={false}
                value={values.start_date}
                onChange={(date) => {
                  setValues((prev) => ({
                    ...prev,
                    start_date: date,
                    end_date: dayjs(date).add(30, 'day'),
                  }));
                }}
                minDate={dayjs(new Date())}
                fullWidth={true}
              />
            </Label>
            <Label
              label="End Date"
              error={touched.end_date && errors.end_date}
              helperText={
                touched.end_date && errors.end_date ? errors.end_date : ""
              }
              required={true}
            >
              <DatePickerInput
                placeholder="Select End Date"
                showNow={false}
                value={
                  values.end_date || dayjs(values.start_date).add(30, "day")
                }
                onChange={() => {}}
                minDate={dayjs(values.start_date)}
                fullWidth={true}
                disabled={true}
              />
            </Label>
            <Label
              label={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span>
                    First Message <span style={{ color: "red" }}>*</span>
                  </span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginLeft: "8px",
                    }}
                  >
                    {/* <Tooltip title="First message should contain the hash key like {{HASH}}.">
                      <i
                        className="fa fa-info-circle"
                        aria-hidden="true"
                        style={{ cursor: "pointer" }}
                      ></i>
                    </Tooltip> */}
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => {
                        const textarea = document.getElementById(
                          "first_message_textarea",
                        );
                        if (textarea) {
                          console.log(
                            "Adding hashtag:",
                            textarea.selectionStart,
                          );

                          const cursorPosition = textarea.selectionStart;
                          const textBeforeCursor = textarea.value.substring(
                            0,
                            cursorPosition,
                          );
                          const textAfterCursor =
                            textarea.value.substring(cursorPosition);
                          const updatedText = `${textBeforeCursor}${
                            "#" + hashTerm + ""
                          } ${textAfterCursor}`;
                          textarea.value = updatedText; // Update the text area value
                          setFieldValue("first_message", updatedText); // Update Formik value
                          textarea.focus(); // Refocus the text area
                          textarea.setSelectionRange(
                            cursorPosition + 1,
                            cursorPosition + 1,
                          ); // Move cursor after the inserted hashtag
                        }
                      }}
                    >
                      Add
                    </Button>
                  </div>
                </div>
              }
              error={touched.first_message && errors.first_message}
              helperText={
                touched.first_message && errors.first_message
                  ? errors.first_message
                  : ""
              }
            >
              <Input.TextArea
                id="first_message_textarea" // Add an ID to reference the text area
                placeholder="Enter First Message"
                {...getFieldProps("first_message")}
                rows={4}
                value={values.first_message || FIRST_MESSAGE} // Ensure the value is controlled by Formik
              />
            </Label>
          </Form>
        </FormikProvider>
      </Modal>
    </>
  );
}
